import {
  AlertOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  NotificationOutlined,
  OrderedListOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { App as AntdApp } from 'antd';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { setAntdAppApis } from './utils/antdApp';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const resolveApiBaseUrl = () => {
  const envApi =
    (process.env.REACT_APP_API_URL as string | undefined) ||
    (process.env.UMI_APP_API_URL as string | undefined) ||
    '';
  if (envApi) return envApi.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host.endsWith('.umicloud.ai')) return 'https://api.umicloud.ai';
  }
  return '';
};

const AntdAppRegistrar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { message, notification } = AntdApp.useApp();

  React.useEffect(() => {
    setAntdAppApis(message, notification);
  }, [message, notification]);

  return <>{children}</>;
};

export const rootContainer = (container: React.ReactNode) => (
  <AntdApp>
    <AntdAppRegistrar>{container}</AntdAppRegistrar>
  </AntdApp>
);

const menuVisualByPath: Record<string, React.ReactNode> = {
  '/dashboard/workplace': <DashboardOutlined style={{ color: '#1677ff' }} />,
  '/business/orders': <OrderedListOutlined style={{ color: '#13c2c2' }} />,
  '/business/arbitrations': <SafetyCertificateOutlined style={{ color: '#fa8c16' }} />,
  '/business/ads': <AppstoreOutlined style={{ color: '#eb2f96' }} />,
  '/business/ads/slots': <AppstoreOutlined style={{ color: '#eb2f96' }} />,
  '/business/ads/campaigns': <AppstoreOutlined style={{ color: '#eb2f96' }} />,
  '/business/notifications': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/notifications/templates': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/notifications/events': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/telegram/jobs': <RobotOutlined style={{ color: '#52c41a' }} />,
  '/business/operations': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/operations/notifications': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/operations/notifications/templates': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/operations/notifications/events': <NotificationOutlined style={{ color: '#722ed1' }} />,
  '/business/operations/telegram/jobs': <RobotOutlined style={{ color: '#52c41a' }} />,
  '/business/operations/telegram-config': <RobotOutlined style={{ color: '#3f6600' }} />,
  '/system/menus': <ApartmentOutlined style={{ color: '#2f54eb' }} />,
  '/system/roles': <UserSwitchOutlined style={{ color: '#531dab' }} />,
  '/system/permissions': <AlertOutlined style={{ color: '#1d39c4' }} />,
  '/system/accounts': <TeamOutlined style={{ color: '#08979c' }} />,
  '/system/audit-logs': <OrderedListOutlined style={{ color: '#d46b08' }} />,
  '/system/telegram-config': <RobotOutlined style={{ color: '#3f6600' }} />,
  '/system/settings': <SettingOutlined style={{ color: '#cf1322' }} />,
};

const getMenuVisual = (path?: string) => {
  if (!path) {
    return <SettingOutlined style={{ color: '#1677ff' }} />;
  }
  return menuVisualByPath[path] || <SettingOutlined style={{ color: '#1677ff' }} />;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 动态获取菜单
    menu: {
      locale: false,
      request: async () => {
        try {
          const apiBase = resolveApiBaseUrl();
          const url = `${apiBase}/api/admin/menus`;
          const res = await fetch(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('umi_admin_token')}`
            }
          });
          const json = await res.json();
          
          // 如果后端返回了标准结构 { success: true, data: [...] }
          const data = Array.isArray(json) ? json : json.data;

          if (!data || !Array.isArray(data)) {
            console.error('Invalid menu data format from backend', json);
            return [];
          }
          const normalizeMenus = (menus: any[]): any[] => {
            const visibleMenus = menus
              .filter((m) => !m.isHidden)
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

            return visibleMenus
              .map((menu) => {
                const children = Array.isArray(menu.children) ? normalizeMenus(menu.children) : [];

                // 目录节点允许无 path；叶子节点必须有 path
                if (!menu.name) {
                  return null;
                }

                if (!menu.path && children.length === 0) {
                  return null;
                }

                return {
                  path: menu.path || `/${menu.code}`,
                  name: menu.name,
                  icon: getMenuVisual(menu.path),
                  ...(children.length > 0 ? { children } : {}),
                };
              })
              .filter(Boolean);
          };

          return normalizeMenus(data);
        } catch (error) {
          console.error('Fetch dynamic menus failed:', error);
          return [];
        }
      },
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
